import type { HistoryEntry, Seat } from '../api/types';
import { useI18n } from '../i18n';
import { outcomeFor } from '../match/machine';
import { ACTION_GLYPH, actionLabelKey } from './ChoiceButtons';

interface Props {
  history: HistoryEntry[];
  mySeat: Seat | null;
  opponentSeatNo: Seat | null;
}

/**
 * 完整對戰紀錄。只吃**已結算**的回合（history 只有 round_resolved 之後才會 append），
 * 所以這個元件天生不可能洩漏進行中的回合。
 *
 * 注意：C01 §5 的 `reveal_history` 旋鈕可以是 `own_only` 或 `none`；
 * 那種情況下後端送來的 `actions` 只會有我這一格，這裡自然就顯示 `—`。
 */
export function HistoryTable({ history, mySeat, opponentSeatNo }: Props) {
  const { t } = useI18n();
  if (history.length === 0) return null;

  return (
    <div className="table-scroll">
      <table className="history-table">
        <caption className="sr-only">{t('match.history')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('match.history.round')}</th>
            <th scope="col">{t('match.history.you')}</th>
            <th scope="col">{t('match.history.opponent')}</th>
            <th scope="col">{t('match.history.result')}</th>
          </tr>
        </thead>
        <tbody>
          {history.map((row) => {
            const mine = mySeat === null ? undefined : row.actions[String(mySeat)];
            const theirs = opponentSeatNo === null ? undefined : row.actions[String(opponentSeatNo)];
            const outcome = outcomeFor(mySeat, row.winner);
            return (
              <tr key={row.round_no}>
                <td>{row.round_no}</td>
                <td>{mine ? `${ACTION_GLYPH[mine] ?? ''} ${t(actionLabelKey(mine))}` : '—'}</td>
                <td>{theirs ? `${ACTION_GLYPH[theirs] ?? ''} ${t(actionLabelKey(theirs))}` : '—'}</td>
                <td>
                  <span className={`badge badge--${outcome}`}>
                    {t(outcome === 'win' ? 'match.result.win' : outcome === 'lose' ? 'match.result.lose' : 'match.result.draw')}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
